import React, { useState, Component } from 'react';
import { X, Upload, FileText, Check, Download, AlertCircle, Loader2, CheckCircle, Shield } from 'lucide-react';
import { AustriaFlag, GermanyFlag, FranceFlag } from '../icons/CountryFlags';
interface NuovaRichiestaModalProps {
  isOpen: boolean;
  onClose: () => void;
}
type Country = 'austria' | 'germany' | 'france' | null;
interface ValidationError {
  field: string;
  message: string;
}
interface FileValidationState {
  isValidating: boolean;
  isValid: boolean | null;
  errors: ValidationError[];
}
const countries = [{
  id: 'austria' as const,
  name: 'Austria',
  FlagComponent: AustriaFlag
}, {
  id: 'germany' as const,
  name: 'Germania',
  FlagComponent: GermanyFlag
}, {
  id: 'france' as const,
  name: 'Francia',
  FlagComponent: FranceFlag
}];
export function NuovaRichiestaModal({
  isOpen,
  onClose
}: NuovaRichiestaModalProps) {
  const [step, setStep] = useState(1);
  const [selectedCountry, setSelectedCountry] = useState<Country>(null);
  const [cantiereFile, setCantiereFile] = useState<File | null>(null);
  const [lavoratoriFile, setLavoratoriFile] = useState<File | null>(null);
  const [contributionVerified, setContributionVerified] = useState(false);
  const [cantiereValidation, setCantiereValidation] = useState<FileValidationState>({
    isValidating: false,
    isValid: null,
    errors: []
  });
  const [lavoratoriValidation, setLavoratoriValidation] = useState<FileValidationState>({
    isValidating: false,
    isValid: null,
    errors: []
  });
  if (!isOpen) return null;
  const handleCountrySelect = (countryId: Country) => {
    setSelectedCountry(countryId);
  };
  // Simulate PDF validation
  const validatePDF = async (file: File, type: 'cantiere' | 'lavoratori'): Promise<{
    isValid: boolean;
    errors: ValidationError[];
  }> => {
    // Simulate API call delay
    await new Promise(resolve => setTimeout(resolve, 2000));
    // Simulate random validation (in real app, this would be actual PDF validation)
    const hasErrors = Math.random() > 0.7; // 30% chance of errors for demo
    if (hasErrors) {
      const errors: ValidationError[] = type === 'cantiere' ? [{
        field: 'Indirizzo cantiere',
        message: 'Campo obbligatorio mancante'
      }, {
        field: 'Data inizio lavori',
        message: 'Formato data non valido'
      }] : [{
        field: 'Codice fiscale lavoratore 1',
        message: 'Codice fiscale non valido'
      }, {
        field: 'Data di nascita lavoratore 2',
        message: 'Campo obbligatorio mancante'
      }];
      return {
        isValid: false,
        errors
      };
    }
    return {
      isValid: true,
      errors: []
    };
  };
  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>, type: 'cantiere' | 'lavoratori') => {
    const file = e.target.files?.[0];
    if (file && file.type === 'application/pdf') {
      if (type === 'cantiere') {
        setCantiereFile(file);
        setCantiereValidation({
          isValidating: true,
          isValid: null,
          errors: []
        });
        const result = await validatePDF(file, 'cantiere');
        setCantiereValidation({
          isValidating: false,
          isValid: result.isValid,
          errors: result.errors
        });
      } else {
        setLavoratoriFile(file);
        setLavoratoriValidation({
          isValidating: true,
          isValid: null,
          errors: []
        });
        const result = await validatePDF(file, 'lavoratori');
        setLavoratoriValidation({
          isValidating: false,
          isValid: result.isValid,
          errors: result.errors
        });
      }
    }
  };
  const handleNext = () => {
    if (step === 1 && selectedCountry) {
      setStep(2);
    } else if (step === 2 && cantiereValidation.isValid) {
      setStep(3);
    } else if (step === 3 && lavoratoriValidation.isValid) {
      setStep(4);
    }
  };
  const handleBack = () => {
    if (step === 4) {
      setStep(3);
    } else if (step === 3) {
      setStep(2);
    } else if (step === 2) {
      setStep(1);
    }
  };
  const handleSubmit = () => {
    console.log('Submitting:', {
      selectedCountry,
      cantiereFile,
      lavoratoriFile,
      contributionVerified
    });
    handleClose();
  };
  const handleClose = () => {
    setStep(1);
    setSelectedCountry(null);
    setCantiereFile(null);
    setLavoratoriFile(null);
    setContributionVerified(false);
    setCantiereValidation({
      isValidating: false,
      isValid: null,
      errors: []
    });
    setLavoratoriValidation({
      isValidating: false,
      isValid: null,
      errors: []
    });
    onClose();
  };
  const handleDownloadTemplate = (type: 'cantiere' | 'lavoratori') => {
    console.log(`Downloading ${type} template for ${selectedCountry}`);
    alert(`Download del modulo ${type === 'cantiere' ? 'Dati Cantiere' : 'Dati Lavoratori'} per ${selectedCountry}`);
  };
  const canProceedStep1 = selectedCountry !== null;
  const canProceedStep2 = cantiereFile && cantiereValidation.isValid === true && !cantiereValidation.isValidating;
  const canProceedStep3 = lavoratoriFile && lavoratoriValidation.isValid === true && !lavoratoriValidation.isValidating;
  const canProceedStep4 = contributionVerified;
  const getStepTitle = () => {
    if (step === 1) return 'Seleziona il paese';
    if (step === 2) return 'Dati del Cantiere';
    if (step === 3) return 'Dati dei Lavoratori';
    return 'Verifica Contributiva';
  };
  return <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-id="element-145">
      {/* Backdrop */}
      <div className="fixed inset-0 bg-black bg-opacity-50" onClick={handleClose} data-id="element-146" />

      {/* Modal */}
      <div className="relative bg-white rounded-lg shadow-xl w-full max-w-2xl flex flex-col" style={{
      maxHeight: '90vh'
    }} data-id="element-147">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200 flex-shrink-0" data-id="element-148">
          <div data-id="element-149">
            <h2 className="text-xl font-semibold text-gray-900" data-id="element-150">
              Nuova Richiesta di Esonero
            </h2>
            <p className="text-sm text-gray-600 mt-1" data-id="element-151">
              Step {step} di 4: {getStepTitle()}
            </p>
          </div>
          <button onClick={handleClose} className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors" data-id="element-152">
            <X size={20} data-id="element-153" />
          </button>
        </div>

        {/* Stepper */}
        <div className="px-6 pt-4 flex-shrink-0" data-id="element-154">
          <div className="flex items-center" data-id="element-155">
            {/* Step 1 */}
            <div className="flex items-center flex-1" data-id="element-156">
              <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${step >= 1 ? 'bg-blue-600 text-white' : 'bg-gray-200 text-gray-600'}`} data-id="element-157">
                {step > 1 ? <Check size={16} data-id="element-158" /> : '1'}
              </div>
              <div className="flex-1 h-1 mx-2 bg-gray-200" data-id="element-159">
                <div className={`h-full transition-all duration-300 ${step >= 2 ? 'bg-blue-600' : 'bg-gray-200'}`} style={{
                width: step >= 2 ? '100%' : '0%'
              }} data-id="element-160" />
              </div>
            </div>

            {/* Step 2 */}
            <div className="flex items-center flex-1" data-id="element-161">
              <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${step >= 2 ? 'bg-blue-600 text-white' : 'bg-gray-200 text-gray-600'}`} data-id="element-162">
                {step > 2 ? <Check size={16} data-id="element-163" /> : '2'}
              </div>
              <div className="flex-1 h-1 mx-2 bg-gray-200" data-id="element-164">
                <div className={`h-full transition-all duration-300 ${step >= 3 ? 'bg-blue-600' : 'bg-gray-200'}`} style={{
                width: step >= 3 ? '100%' : '0%'
              }} data-id="element-165" />
              </div>
            </div>

            {/* Step 3 */}
            <div className="flex items-center flex-1" data-id="element-166">
              <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${step >= 3 ? 'bg-blue-600 text-white' : 'bg-gray-200 text-gray-600'}`} data-id="element-167">
                {step > 3 ? <Check size={16} data-id="element-168" /> : '3'}
              </div>
              <div className="flex-1 h-1 mx-2 bg-gray-200" data-id="element-169">
                <div className={`h-full transition-all duration-300 ${step >= 4 ? 'bg-blue-600' : 'bg-gray-200'}`} style={{
                width: step >= 4 ? '100%' : '0%'
              }} data-id="element-170" />
              </div>
            </div>

            {/* Step 4 */}
            <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${step >= 4 ? 'bg-blue-600 text-white' : 'bg-gray-200 text-gray-600'}`} data-id="element-171">
              4
            </div>
          </div>
        </div>

        {/* Content - scrollable */}
        <div className="p-6 overflow-y-auto flex-1" data-id="element-172">
          {/* Step 1: Country Selection */}
          {step === 1 && <div className="space-y-4" data-id="element-173">
              <p className="text-gray-700 mb-6" data-id="element-174">
                Seleziona il paese di destinazione del lavoratore distaccato
              </p>
              <div className="grid grid-cols-3 gap-4" data-id="element-175">
                {countries.map(country => <button key={country.id} onClick={() => handleCountrySelect(country.id)} className={`p-6 border-2 rounded-lg transition-all ${selectedCountry === country.id ? 'border-blue-600 bg-blue-50' : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'}`} data-id="element-176">
                    <div className="w-full h-20 mb-3 rounded-lg overflow-hidden shadow-sm" data-id="element-177">
                      <country.FlagComponent className="w-full h-full object-cover" data-id="element-178" />
                    </div>
                    <div className="text-lg font-medium text-gray-900" data-id="element-179">
                      {country.name}
                    </div>
                  </button>)}
              </div>
            </div>}

          {/* Step 2: Cantiere File Upload */}
          {step === 2 && <div className="space-y-6" data-id="element-180">
              <p className="text-gray-700 mb-4" data-id="element-181">
                Scarica il modulo, compilalo e caricalo per la validazione
                automatica
              </p>

              <div data-id="element-182">
                <div className="flex items-center justify-between mb-2" data-id="element-183">
                  <label className="block text-sm font-medium text-gray-700" data-id="element-184">
                    Dati del Cantiere *
                  </label>
                  <button onClick={() => handleDownloadTemplate('cantiere')} className="flex items-center gap-1 text-sm text-blue-600 hover:text-blue-700 font-medium" data-id="element-185">
                    <Download size={16} data-id="element-186" />
                    Scarica modulo da compilare
                  </button>
                </div>

                <div className={`border-2 border-dashed rounded-lg p-6 text-center transition-colors ${cantiereValidation.isValid === true ? 'border-green-500 bg-green-50' : cantiereValidation.isValid === false ? 'border-red-500 bg-red-50' : 'border-gray-300 hover:border-gray-400'}`} data-id="element-187">
                  <input type="file" accept=".pdf" onChange={e => handleFileChange(e, 'cantiere')} className="hidden" id="cantiere-upload" disabled={cantiereValidation.isValidating} data-id="element-188" />
                  <label htmlFor="cantiere-upload" className="cursor-pointer" data-id="element-189">
                    {cantiereValidation.isValidating ? <div className="flex flex-col items-center gap-2" data-id="element-190">
                        <Loader2 size={32} className="text-blue-600 animate-spin" data-id="element-191" />
                        <p className="text-gray-700 font-medium" data-id="element-192">
                          Validazione in corso...
                        </p>
                        <p className="text-xs text-gray-500" data-id="element-193">
                          Controllo automatico del PDF
                        </p>
                      </div> : cantiereValidation.isValid === true ? <div className="flex flex-col items-center gap-2" data-id="element-194">
                        <CheckCircle size={32} className="text-green-600" data-id="element-195" />
                        <p className="text-green-700 font-medium" data-id="element-196">
                          {cantiereFile?.name}
                        </p>
                        <p className="text-xs text-green-600" data-id="element-197">
                          ✓ Validazione completata con successo
                        </p>
                      </div> : cantiereFile ? <div className="flex items-center justify-center gap-2 text-gray-700" data-id="element-198">
                        <FileText size={24} data-id="element-199" />
                        <span className="font-medium" data-id="element-200">{cantiereFile.name}</span>
                      </div> : <div data-id="element-201">
                        <Upload size={32} className="mx-auto text-gray-400 mb-2" data-id="element-202" />
                        <p className="text-gray-600" data-id="element-203">
                          Clicca per caricare il modulo compilato dei dati del
                          cantiere
                        </p>
                        <p className="text-xs text-gray-500 mt-1" data-id="element-204">
                          Solo file PDF
                        </p>
                      </div>}
                  </label>
                </div>

                {/* Validation Errors */}
                {cantiereValidation.isValid === false && cantiereValidation.errors.length > 0 && <div className="mt-4 p-4 bg-red-50 border border-red-200 rounded-lg" data-id="element-205">
                      <div className="flex items-start gap-2" data-id="element-206">
                        <AlertCircle size={20} className="text-red-600 flex-shrink-0 mt-0.5" data-id="element-207" />
                        <div className="flex-1" data-id="element-208">
                          <h4 className="text-sm font-semibold text-red-800 mb-2" data-id="element-209">
                            Errori di validazione trovati:
                          </h4>
                          <ul className="space-y-1" data-id="element-210">
                            {cantiereValidation.errors.map((error, index) => <li key={index} className="text-sm text-red-700" data-id="element-211">
                                <span className="font-medium" data-id="element-212">
                                  {error.field}:
                                </span>{' '}
                                {error.message}
                              </li>)}
                          </ul>
                          <p className="text-xs text-red-600 mt-3" data-id="element-213">
                            Correggi gli errori e ricarica il documento per
                            procedere.
                          </p>
                        </div>
                      </div>
                    </div>}
              </div>
            </div>}

          {/* Step 3: Lavoratori File Upload */}
          {step === 3 && <div className="space-y-6" data-id="element-214">
              <p className="text-gray-700 mb-4" data-id="element-215">
                Scarica il modulo, compilalo e caricalo per la validazione
                automatica
              </p>

              <div data-id="element-216">
                <div className="flex items-center justify-between mb-2" data-id="element-217">
                  <label className="block text-sm font-medium text-gray-700" data-id="element-218">
                    Dati dei Lavoratori *
                  </label>
                  <button onClick={() => handleDownloadTemplate('lavoratori')} className="flex items-center gap-1 text-sm text-blue-600 hover:text-blue-700 font-medium" data-id="element-219">
                    <Download size={16} data-id="element-220" />
                    Scarica modulo da compilare
                  </button>
                </div>

                <div className={`border-2 border-dashed rounded-lg p-6 text-center transition-colors ${lavoratoriValidation.isValid === true ? 'border-green-500 bg-green-50' : lavoratoriValidation.isValid === false ? 'border-red-500 bg-red-50' : 'border-gray-300 hover:border-gray-400'}`} data-id="element-221">
                  <input type="file" accept=".pdf" onChange={e => handleFileChange(e, 'lavoratori')} className="hidden" id="lavoratori-upload" disabled={lavoratoriValidation.isValidating} data-id="element-222" />
                  <label htmlFor="lavoratori-upload" className="cursor-pointer" data-id="element-223">
                    {lavoratoriValidation.isValidating ? <div className="flex flex-col items-center gap-2" data-id="element-224">
                        <Loader2 size={32} className="text-blue-600 animate-spin" data-id="element-225" />
                        <p className="text-gray-700 font-medium" data-id="element-226">
                          Validazione in corso...
                        </p>
                        <p className="text-xs text-gray-500" data-id="element-227">
                          Controllo automatico del PDF
                        </p>
                      </div> : lavoratoriValidation.isValid === true ? <div className="flex flex-col items-center gap-2" data-id="element-228">
                        <CheckCircle size={32} className="text-green-600" data-id="element-229" />
                        <p className="text-green-700 font-medium" data-id="element-230">
                          {lavoratoriFile?.name}
                        </p>
                        <p className="text-xs text-green-600" data-id="element-231">
                          ✓ Validazione completata con successo
                        </p>
                      </div> : lavoratoriFile ? <div className="flex items-center justify-center gap-2 text-gray-700" data-id="element-232">
                        <FileText size={24} data-id="element-233" />
                        <span className="font-medium" data-id="element-234">
                          {lavoratoriFile.name}
                        </span>
                      </div> : <div data-id="element-235">
                        <Upload size={32} className="mx-auto text-gray-400 mb-2" data-id="element-236" />
                        <p className="text-gray-600" data-id="element-237">
                          Clicca per caricare il modulo compilato dei dati dei
                          lavoratori
                        </p>
                        <p className="text-xs text-gray-500 mt-1" data-id="element-238">
                          Solo file PDF
                        </p>
                      </div>}
                  </label>
                </div>

                {/* Validation Errors */}
                {lavoratoriValidation.isValid === false && lavoratoriValidation.errors.length > 0 && <div className="mt-4 p-4 bg-red-50 border border-red-200 rounded-lg" data-id="element-239">
                      <div className="flex items-start gap-2" data-id="element-240">
                        <AlertCircle size={20} className="text-red-600 flex-shrink-0 mt-0.5" data-id="element-241" />
                        <div className="flex-1" data-id="element-242">
                          <h4 className="text-sm font-semibold text-red-800 mb-2" data-id="element-243">
                            Errori di validazione trovati:
                          </h4>
                          <ul className="space-y-1" data-id="element-244">
                            {lavoratoriValidation.errors.map((error, index) => <li key={index} className="text-sm text-red-700" data-id="element-245">
                                <span className="font-medium" data-id="element-246">
                                  {error.field}:
                                </span>{' '}
                                {error.message}
                              </li>)}
                          </ul>
                          <p className="text-xs text-red-600 mt-3" data-id="element-247">
                            Correggi gli errori e ricarica il documento per
                            procedere.
                          </p>
                        </div>
                      </div>
                    </div>}
              </div>
            </div>}

          {/* Step 4: Contribution Verification */}
          {step === 4 && <div className="space-y-6" data-id="element-248">
              <p className="text-gray-700 mb-4" data-id="element-249">
                Verifica la regolarità contributiva dell'azienda
              </p>

              <label className="flex items-start gap-3 cursor-pointer p-4 border-2 border-gray-300 rounded-lg hover:border-blue-400 transition-colors" data-id="element-250">
                <input type="checkbox" checked={contributionVerified} onChange={e => setContributionVerified(e.target.checked)} className="w-5 h-5 text-blue-600 border-gray-300 rounded focus:ring-blue-500 flex-shrink-0 mt-0.5" data-id="element-251" />
                <span className="text-sm text-gray-700" data-id="element-252">
                  <span className="font-semibold" data-id="element-253">
                    Dichiaro di aver verificato che la situazione contributiva
                    dell'azienda è in regola
                  </span>
                  <span className="block text-xs text-gray-600 mt-1" data-id="element-254">
                    Questa dichiarazione è obbligatoria per procedere con
                    l'invio della richiesta di esonero.
                  </span>
                </span>
              </label>
            </div>}
        </div>

        {/* Footer - fixed at bottom */}
        <div className="flex items-center justify-between p-6 border-t border-gray-200 bg-gray-50 flex-shrink-0 rounded-b-lg" data-id="element-255">
          <button onClick={handleClose} className="px-4 py-2 text-gray-700 hover:bg-gray-200 rounded-lg transition-colors" data-id="element-256">
            Annulla
          </button>
          <div className="flex gap-2" data-id="element-257">
            {step > 1 && <button onClick={handleBack} className="px-4 py-2 text-gray-700 border border-gray-300 hover:bg-gray-50 rounded-lg transition-colors" data-id="element-258">
                Indietro
              </button>}
            {step < 4 ? <button onClick={handleNext} disabled={step === 1 && !canProceedStep1 || step === 2 && !canProceedStep2 || step === 3 && !canProceedStep3} className={`px-4 py-2 text-white rounded-lg transition-colors ${step === 1 && canProceedStep1 || step === 2 && canProceedStep2 || step === 3 && canProceedStep3 ? 'hover:opacity-90' : 'opacity-50 cursor-not-allowed'}`} style={{
            backgroundColor: step === 1 && canProceedStep1 || step === 2 && canProceedStep2 || step === 3 && canProceedStep3 ? '#172637' : '#94a3b8'
          }} data-id="element-259">
                Avanti
              </button> : <button onClick={handleSubmit} disabled={!canProceedStep4} className={`px-4 py-2 text-white rounded-lg transition-colors ${canProceedStep4 ? 'hover:opacity-90' : 'opacity-50 cursor-not-allowed'}`} style={{
            backgroundColor: canProceedStep4 ? '#172637' : '#94a3b8'
          }} data-id="element-260">
                Invia Richiesta
              </button>}
          </div>
        </div>
      </div>
    </div>;
}